import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, onSnapshot, query, where, orderBy, addDoc, serverTimestamp, Timestamp, collection } from 'firebase/firestore';
import { ArrowLeft, Plus, Trash2, Phone, MessageSquare, Mail, ClipboardList, Settings, Undo2, X, Archive } from 'lucide-react';
import { customersCol, jobsCol } from './database.js';
import { db, auth } from './firebase.js';
import { updateOne, deleteOne, markJobAsPaid, cancelJob, archiveCustomer, restoreCustomer } from './lib/db-crud.js';
import { useBusinessProfile, businessSnapshot } from './lib/use-business-profile.js';
import { formatPrice, formatPhone, randomToken, openSms, openMailto, STATUS_ORDER, STATUS_TEXT, isTerminal, isCancelled, isArchived } from './lib/job-helpers.js';
import Vehicles, { useCustomerVehicles } from './Vehicles.jsx';
import CustomerModal from './CustomerModal.jsx';
import JobLeaveModal from './JobLeaveModal.jsx';
import ConfirmDialog from './ConfirmDialog.jsx';

import DeclinedWorkPanel from './DeclinedWorkPanel.jsx';
import CancelJobModal from './CancelJobModal.jsx';
import { purgeJobAssets } from './lib/job-assets.js';

import { buildRecommendationsSnapshot, applyDecisions } from './lib/recommendations.js';
import {
  buildJobPhotosSnapshot, readInspectionSnapshot, buildTaxSnapshot,
} from './lib/share-snapshot.js';
import { hasJobNumber } from './lib/job-number.js';
import InspectionPanel from './InspectionPanel.jsx';
import { JobCard } from './JobCards.jsx';
import { JobModal } from './JobModal.jsx';

// Status lifecycle ranking. The approval/payment listeners use this to
// guarantee FORWARD-ONLY transitions: a snapshot delivery (especially
// the initial-mount emission, which fires 'added' for every existing
// doc) must never move a job backward. Without this, a previously-paid
// job would silently revert to 'approved' the next time Jobs.jsx
// mounted and the estimate listener re-applied to that job's still-
// approved sharedEstimates doc. That's what caused the 'paid jobs come
// back after refresh / add' symptom on 2026-05-13.
const STATUS_RANK = { draft: 0, sent: 1, approved: 2, invoiced: 3, paid: 4 };

// When a customer approves a share doc OR marks an invoice paid, sync
// the change back to the matching job. Read-then-update so we only
// ever advance the status, never regress it.
function useApprovalSync() {
  useEffect(() => {
    if (!auth.currentUser) return;

    const estimatesQ = query(
      collection(db, 'sharedEstimates'),
      where('userId', '==', auth.currentUser.uid)
    );
    const unsubEstimates = onSnapshot(estimatesQ, (snap) => {
      snap.docChanges().forEach(async (change) => {
        if (change.type === 'removed') return;
        const share = change.doc.data();
        if (!share.jobId) return;
        if (!share.approved && !share.declined) return;
        try {
          // Read current job state. Only advance to 'approved' if the
          // job is currently in 'draft' or 'sent'. If it's already
          // approved / invoiced / paid, skip — the customer's approval
          // is in the past and we'd be undoing later progress.
          const jobRef = doc(jobsCol(), share.jobId);
          const jobSnap = await getDoc(jobRef);
          if (!jobSnap.exists()) return;
          const job = jobSnap.data();

          // A cancelled job is terminal. Without this guard its status
          // isn't on STATUS_RANK, so `?? 0` would read it as a draft
          // and the listener's initial emission would quietly drag it
          // back to 'approved' on the next page mount.
          if (isCancelled(job)) return;

          // The customer said no to the whole estimate.
          if (share.declined) {
            await cancelJob(jobsCol(), { id: share.jobId, ...job }, {
              reason: 'customer-declined',
              note: '',
              by: 'customer',
            });
            return;
          }

          const currentRank = STATUS_RANK[job.status] ?? 0;
          if (currentRank >= STATUS_RANK.approved) return;

          // Fold the customer's per-line answers back onto the job, so
          // the accepted work is what the mechanic sees and the
          // declined work is on file for next time. Matching is by
          // recommendation id, which the share doc carries verbatim.
          const decisions = share.recommendationDecisions;
          const decided = decisions
            ? applyDecisions(job.recommendations, decisions)
            : null;

          await updateOne(jobsCol(), share.jobId, {
            status: 'approved',
            approvedAt: share.approvedAt || serverTimestamp(),
            ...(decided ? {
              recommendations: decided,
              decidedAt: share.approvedAt || serverTimestamp(),
            } : {}),
          });
        } catch (_) { /* job deleted, ignore */ }
      });
    });

    const invoicesQ = query(
      collection(db, 'sharedInvoices'),
      where('userId', '==', auth.currentUser.uid)
    );
    const unsubInvoices = onSnapshot(invoicesQ, (snap) => {
      snap.docChanges().forEach(async (change) => {
        if (change.type === 'removed') return;
        const inv = change.doc.data();
        if (!inv.paid || !inv.jobId) return;
        try {
          // Idempotency guard: if the job is already paid AND closed,
          // skip — otherwise the listener's initial emission would
          // churn closedAt to "now" on every customer-detail-page mount.
          const jobRef = doc(jobsCol(), inv.jobId);
          const jobSnap = await getDoc(jobRef);
          if (!jobSnap.exists()) return;
          const job = jobSnap.data();
          if (isCancelled(job)) return;   // terminal — never revive
          if (job.status === 'paid' && job.closedAt) return;
          await updateOne(jobsCol(), inv.jobId, {
            status:   'paid',
            paidAt:   inv.paidAt || serverTimestamp(),
            // Customer-paid-via-link should ALSO close the ticket so it
            // lands in History — same reasoning as manual Mark Paid.
            closedAt: serverTimestamp(),
            closedBy: 'customer-paid',
            awaitingReturn: false,
            awaitingPart:   false,
          });
        } catch (_) { /* job deleted, ignore */ }
      });
    });

    return () => { unsubEstimates(); unsubInvoices(); };
  }, []);
}

// Status taxonomy + small format helpers live in lib/job-helpers.js so
// Outstanding.jsx and History.jsx can reuse them.

export default function Jobs() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Home's + NEW → Vehicle picker navigates here with ?addVehicle=1
  // — read once and pass down to Vehicles so it auto-opens the modal.
  const autoOpenAddVehicle = searchParams.get('addVehicle') === '1';
  const [customer, setCustomer] = useState(null);
  const [customerLoadFailed, setCustomerLoadFailed] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoaded, setJobsLoaded] = useState(false);
  const [modalState, setModalState] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | one of STATUS_ORDER
  const [leaveJob,     setLeaveJob]     = useState(null);  // job currently being Left
  const [pendingStart, setPendingStart] = useState(null);  // job awaiting "not approved" confirmation
  const [pendingMarkPaid, setPendingMarkPaid] = useState(null); // job awaiting Mark Paid confirmation
  const [pendingDeleteCustomer, setPendingDeleteCustomer] = useState(null); // customer awaiting delete confirmation
  const [inspectJob, setInspectJob] = useState(null);      // job whose inspection is open
  const [cancelJobTarget, setCancelJob] = useState(null);  // job awaiting cancel confirmation
  const { vehicles } = useCustomerVehicles(customerId);
  const { profile: businessProfile } = useBusinessProfile();

  useApprovalSync();

  const vehicleById = Object.fromEntries(vehicles.map((v) => [v.id, v]));

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const snap = await getDoc(doc(customersCol(), customerId));
        if (cancelled) return;
        if (!snap.exists()) setCustomerLoadFailed(true);
        else setCustomer({ id: snap.id, ...snap.data() });
      } catch (e) {
        console.error('Customer load failed:', e);
        if (!cancelled) setCustomerLoadFailed(true);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [customerId]);

  useEffect(() => {
    const q = query(
      jobsCol(),
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        // serverTimestamps: 'estimate' fills pending serverTimestamp()
        // writes (startedAt, sentAt, paidAt, etc.) with a local-time
        // estimate so UI buttons that branch on `!!job.startedAt` flip
        // immediately on the first emission instead of waiting for the
        // server confirmation round-trip.
        setJobs(snap.docs.map((d) => ({ id: d.id, ...d.data({ serverTimestamps: 'estimate' }) })));
        setJobsLoaded(true);
      },
      (err) => {
        console.error('Jobs load failed:', err);
        setJobsLoaded(true);
      }
    );
    return unsub;
  }, [customerId]);

  // Active jobs = anything not finished with. Fully-closed (paid) and
  // cancelled jobs both live in /history. A closed-but-unpaid job stays
  // here so you can still mark it paid or resend the invoice.
  const activeJobs = jobs.filter((j) => !isTerminal(j));

  // Per-status counts for the stats panel + filter pills
  const statusCounts = STATUS_ORDER.reduce((acc, s) => {
    acc[s] = activeJobs.filter((j) => j.status === s).length;
    return acc;
  }, {});
  const total = activeJobs.length;
  const filteredJobs = statusFilter === 'all'
    ? activeJobs
    : activeJobs.filter((j) => j.status === statusFilter);
  const closedCount = jobs.length - activeJobs.length;

  function openAdd() {
    if (vehicles.length === 0) {
      alert('Add at least one vehicle before creating a job.');
      return;
    }
    setModalState({
      description: '',
      vehicleId: vehicles[0]?.id || '',
      lineItems: [],
      shopSupplies: 0,
      scheduledAt: null,
      durationMinutes: null,
    });
  }
  function openEdit(j) {
    setModalState({
      id: j.id,
      description: j.description,
      vehicleId: j.vehicleId || (vehicles[0]?.id || ''),
      lineItems: j.lineItems || [],
      shopSupplies: Number(j.shopSupplies) || 0,
      scheduledAt: j.scheduledAt || null,
      durationMinutes: j.durationMinutes || null,
    });
  }

  async function handleDelete(j) {
    if (!confirm(`Delete this job?\n\n"${j.description}"\n\nThis can't be undone.`)) return;
    try {
      // Photos and the inspection go with it. Without this they'd stay
      // in Storage forever — unreachable and still billed.
      await purgeJobAssets(j.id, j);
      await deleteOne(jobsCol(), j.id);
    }
    catch (e) { alert(`Could not delete: ${e.message}`); }
  }

  // Money still owed doesn't stop you archiving someone, but you get
  // told. Hiding an unpaid invoice without a word is how a mechanic
  // loses track of a debt and blames the app for it.
  const archiveImpact = useMemo(() => {
    const owed = jobs
      .filter((j) => j.status === 'invoiced')
      .reduce((sum, j) => sum + (Number(j.totalPrice) || 0), 0);
    return { owed, open: jobs.filter((j) => !isTerminal(j)).length };
  }, [jobs]);

  // Archiving replaces what used to be a permanent cascade delete: one
  // confirm removed the customer, every vehicle and every job forever.
  // Now it disappears from everywhere you work and can be brought back
  // from Customers → Archived. Permanent deletion still exists, but
  // only from there, and only after an export is offered.
  function handleDeleteCustomer() {
    if (!customer) return;
    setPendingDeleteCustomer(customer);
  }

  async function handleRestoreCustomer() {
    if (!customer) return;
    try {
      await restoreCustomer(customersCol(), customer);
    } catch (e) {
      console.error('Restore failed:', e);
      alert(`Could not restore: ${e.message}`);
    }
  }

  async function actuallyArchiveCustomer(c) {
    if (!c) return;
    try {
      await archiveCustomer(customersCol(), c);
      navigate('/customers');
    } catch (e) {
      console.error('Archive failed:', e);
      alert(`Could not archive: ${e.message}`);
    }
  }

  // channel: 'text' | 'email'. We always create the share doc (so the URL
  // exists + business info gets snapshotted) before opening the channel.
  async function handleSendEstimate(j, channel = 'text') {
    if (!customer) { alert('Customer info still loading. Try again in a moment.'); return; }
    if (channel === 'email' && !customer.email) {
      alert('No email on file for this customer. Add one from the Edit screen, or send by text.');
      return;
    }
    try {
      const approvalToken = randomToken();
      const jobPhotos = buildJobPhotosSnapshot(j);
      const inspectionReport = await readInspectionSnapshot(j.id);
      const recs = buildRecommendationsSnapshot(j.recommendations);
      const sharedRef = await addDoc(collection(db, 'sharedEstimates'), {
        userId: auth.currentUser.uid,
        jobId: j.id,
        // customerId lets the push-notification Cloud Function build
        // a deep-link back to /customers/{id} when the customer
        // approves. Optional from a rendering standpoint.
        customerId: j.customerId || '',
        customerName: customer.name,
        description: j.description,
        totalPrice: j.totalPrice,
        // The ticket's number, carried onto the customer-facing doc.
        // Same digits as the eventual invoice — see lib/job-number.js.
        ...(hasJobNumber(j) ? { jobNumber: j.jobNumber } : {}),
        // Snapshot the parts/labor breakdown so the public estimate
        // page can render the line items. Persisted on the share doc
        // (not pulled live from the job) so a later job edit can't
        // silently change what the customer saw when they approved.
        lineItems: Array.isArray(j.lineItems) ? j.lineItems : [],
        shopSupplies: Number(j.shopSupplies) || 0,
        // Sales tax frozen as it stood when this was sent. Absent
        // entirely on an untaxed job. See buildTaxSnapshot.
        ...buildTaxSnapshot(j),
        ...(jobPhotos ? { jobPhotos } : {}),
        ...(inspectionReport ? { inspectionReport } : {}),
        // The customer decides on these line by line. Sent without a
        // status so re-sending an edited estimate always starts the
        // decision fresh rather than carrying an old answer forward.
        ...(recs ? { recommendations: recs } : {}),
        ...businessSnapshot(businessProfile),
        approvalToken,
        approved: false,
        approvedAt: null,
        createdAt: serverTimestamp(),
      });
      await updateOne(jobsCol(), j.id, {
        shareId: sharedRef.id,
        status: 'sent',
        sentAt: serverTimestamp(),
      });
      const shareUrl = `${window.location.origin}/share/${sharedRef.id}`;
      const body = `Estimate from your mechanic:\n\n${shareUrl}`;
      if (channel === 'email') {
        openMailto(customer.email, 'Estimate from your mechanic', body);
      } else {
        openSms(customer.phone, body);
      }
    } catch (e) {
      console.error('Send Estimate failed:', e);
      alert(`Could not send: ${e.message}`);
    }
  }

  async function handleSendInvoice(j, channel = 'text') {
    if (!customer) { alert('Customer info still loading. Try again in a moment.'); return; }
    if (channel === 'email' && !customer.email) {
      alert('No email on file for this customer. Add one from the Edit screen, or send by text.');
      return;
    }
    try {
      const paymentToken = randomToken();
      const jobPhotos = buildJobPhotosSnapshot(j);
      const inspectionReport = await readInspectionSnapshot(j.id);
      const invRef = await addDoc(collection(db, 'sharedInvoices'), {
        userId: auth.currentUser.uid,
        jobId: j.id,
        customerId: j.customerId || '',
        customerName: customer.name,
        description: j.description,
        totalPrice: j.totalPrice,
        ...(hasJobNumber(j) ? { jobNumber: j.jobNumber } : {}),
        // Same snapshot reasoning as sharedEstimates above.
        lineItems: Array.isArray(j.lineItems) ? j.lineItems : [],
        shopSupplies: Number(j.shopSupplies) || 0,
        // Sales tax frozen as it stood when this was sent. Absent
        // entirely on an untaxed job. See buildTaxSnapshot.
        ...buildTaxSnapshot(j),
        ...(jobPhotos ? { jobPhotos } : {}),
        ...(inspectionReport ? { inspectionReport } : {}),
        ...businessSnapshot(businessProfile),
        paymentToken,
        paid: false,
        paidAt: null,
        createdAt: serverTimestamp(),
      });
      await updateOne(jobsCol(), j.id, {
        invoiceShareId: invRef.id,
        status: 'invoiced',
        invoicedAt: serverTimestamp(),
      });
      const invoiceUrl = `${window.location.origin}/invoice/${invRef.id}`;
      const body = `Invoice from your mechanic:\n\n${invoiceUrl}`;
      if (channel === 'email') {
        openMailto(customer.email, 'Invoice from your mechanic', body);
      } else {
        openSms(customer.phone, body);
      }
    } catch (e) {
      console.error('Send Invoice failed:', e);
      alert(`Could not send: ${e.message}`);
    }
  }

  // Mark Paid opens the themed ConfirmDialog. The actual write happens
  // in actuallyMarkPaid below — also closes the ticket (sets closedAt)
  // so it moves to History.
  function handleMarkPaid(j) {
    setPendingMarkPaid(j);
  }

  async function actuallyMarkPaid(j) {
    if (!j) return;
    try {
      await markJobAsPaid(jobsCol(), j, 'mark-paid');
    } catch (e) {
      alert(`Could not mark paid: ${e.message}`);
    }
  }

  // Start the work timer. Warns via themed ConfirmDialog if the
  // customer hasn't approved yet — sometimes you start on trust before
  // formal approval, so it's a confirm rather than a hard block.
  //
  // startedAt uses Timestamp.now() (NOT serverTimestamp()) so the value
  // is available SYNCHRONOUSLY in the local cache — the Start/Leave
  // button flips the moment Firestore queues the write, not after the
  // server round-trip. Client clock is the right reference for "when
  // you punched in" anyway; this isn't a back-end coordination event.
  function handleStart(j) {
    if (!j) return;
    if (j.status !== 'approved' && j.status !== 'invoiced') {
      setPendingStart(j);
      return;
    }
    actuallyStart(j);
  }

  async function actuallyStart(j) {
    if (!j) return;
    try {
      // closedAt: null re-opens previously-closed jobs.
      await updateOne(jobsCol(), j.id, {
        startedAt:      Timestamp.now(),
        closedAt:       null,
        awaitingReturn: false,
        awaitingPart:   false,
      });
      // Auto-navigate into the active workspace — same destination
      // as Start from Home or Calendar.
      navigate(`/active/${j.id}`);
    } catch (e) {
      alert(`Could not start: ${e.message}`);
    }
  }

  function handleLeave(j) {
    setLeaveJob(j);
  }

  // Undo Start — clears startedAt for the "didn't mean to tap that" case.
  async function handleUndoStart(j) {
    if (!j) return;
    try {
      await updateOne(jobsCol(), j.id, { startedAt: null });
    } catch (e) {
      alert(`Could not undo: ${e.message}`);
    }
  }

  if (customerLoadFailed) {
    return (
      <div>
        <Link to="/customers" className="btn-link back-to-customers"><ArrowLeft size={14} /> Back to customers</Link>
        <div className="empty-state" style={{ marginTop: 16 }}>
          <p style={{ fontWeight: 600 }}>Customer not found.</p>
          <p className="muted small">It may have been deleted.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Link to="/customers" className="btn-link back-to-customers"><ArrowLeft size={14} /> Back to customers</Link>

      {customer && isArchived(customer) && (
        <div className="archived-banner">
          <Archive size={15} />
          <span>
            <strong>{customer.name} is archived.</strong>{' '}
            They're hidden from your lists, calendar and Home. Everything below is
            still here.
          </span>
          <button type="button" className="btn-secondary" onClick={handleRestoreCustomer}>
            <Undo2 size={14} /> Restore
          </button>
        </div>
      )}

      <div className="screen-header" style={{ marginTop: 12 }}>
        <div>
          <div className="customer-name-row">
            <h1>{customer?.name || 'Loading…'}</h1>
            {customer && (
              <>
                <button
                  type="button"
                  className="icon-btn-edit"
                  onClick={() => setEditingCustomer(true)}
                  aria-label="Edit customer info"
                  title="Edit customer info"
                >
                  <Settings size={16} />
                </button>
                {!isArchived(customer) && (
                  <button
                    type="button"
                    className="icon-btn-edit danger"
                    onClick={handleDeleteCustomer}
                    aria-label="Archive customer"
                    title="Archive customer"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </>
            )}
          </div>
          {(customer?.phone || customer?.email) && (
            <div className="contact-row contact-row-compact">
              {customer.phone && (
                <div className="contact-line">
                  <span className="contact-text muted mono small">{formatPhone(customer.phone)}</span>
                  <div className="contact-actions">
                    <a
                      href={`tel:${customer.phone.replace(/\D/g, '')}`}
                      className={`contact-action small ${customer.preferredContact?.includes('phone') ? 'preferred' : ''}`}
                      aria-label={`Call ${customer.name}`}
                      title={customer.preferredContact?.includes('phone') ? 'Call (preferred)' : 'Call'}
                    >
                      <Phone size={14} />
                    </a>
                    <a
                      href={`sms:${customer.phone.replace(/\D/g, '')}`}
                      className={`contact-action small ${customer.preferredContact?.includes('text') ? 'preferred' : ''}`}
                      aria-label={`Text ${customer.name}`}
                      title={customer.preferredContact?.includes('text') ? 'Text (preferred)' : 'Text'}
                    >
                      <MessageSquare size={14} />
                    </a>
                  </div>
                </div>
              )}
              {customer.email && (
                <div className="contact-line">
                  <span className="contact-text muted small">{customer.email}</span>
                  <div className="contact-actions">
                    <a
                      href={`mailto:${customer.email}`}
                      className={`contact-action small ${customer.preferredContact?.includes('email') ? 'preferred' : ''}`}
                      aria-label={`Email ${customer.name}`}
                      title={customer.preferredContact?.includes('email') ? 'Email (preferred)' : 'Email'}
                    >
                      <Mail size={14} />
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {editingCustomer && customer && (
        <CustomerModal
          initial={{
            id: customer.id,
            name: customer.name || '',
            phone: customer.phone || '',
            email: customer.email || '',
            address: customer.address || '',
            notes: customer.notes || '',
            preferredContact: Array.isArray(customer.preferredContact) ? customer.preferredContact : [],
          }}
          onClose={() => setEditingCustomer(false)}
        />
      )}

      <Vehicles
        customerId={customerId}
        autoOpenAdd={autoOpenAddVehicle}
        onAutoOpenConsumed={() => setSearchParams({}, { replace: true })}
      />

      <div className="section-header" style={{ marginTop: 32 }}>
        <h2>Jobs</h2>
        <button
          className="btn"
          onClick={openAdd}
        >
          <Plus size={16} strokeWidth={2.5} />
          Add Job
        </button>
      </div>

      {/* Stats panel — counts by status */}
      {jobsLoaded && total > 0 && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Total</div>
            <div className="stat-value">{total}</div>
          </div>
          {STATUS_ORDER.map((s) => (
            <div key={s} className={`stat-card ${s === 'paid' ? 'success' : s === 'invoiced' ? 'accent' : ''}`}>
              <div className="stat-label">{STATUS_TEXT[s]}</div>
              <div className="stat-value">{statusCounts[s]}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filter pills */}
      {jobsLoaded && total > 0 && (
        <div className="filter-row">
          <button
            type="button"
            className={`filter-pill ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All <span className="count">{total}</span>
          </button>
          {STATUS_ORDER.filter((s) => statusCounts[s] > 0).map((s) => (
            <button
              key={s}
              type="button"
              className={`filter-pill ${statusFilter === s ? 'active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {STATUS_TEXT[s]} <span className="count">{statusCounts[s]}</span>
            </button>
          ))}
        </div>
      )}

      {/* What they've said no to before — collapsed by default so it
          informs the visit without shouting over the active work. */}
      {jobsLoaded && <DeclinedWorkPanel jobs={jobs} vehicles={vehicles} />}

      {!jobsLoaded && <p className="muted small">Loading jobs…</p>}

      {jobsLoaded && total === 0 && (
        <p className="muted small empty-inline">
          <ClipboardList size={20} color="var(--text-3)" strokeWidth={1.5} style={{ display: 'block', margin: '0 auto 6px' }} />
          No jobs for {customer?.name || 'this customer'} yet. Tap <strong>+ Add Job</strong> above.
        </p>
      )}

      {jobsLoaded && total > 0 && filteredJobs.length === 0 && (
        <p className="muted small empty-inline">
          No jobs match the <strong>{STATUS_TEXT[statusFilter]}</strong> filter.
        </p>
      )}

      {jobsLoaded && filteredJobs.length > 0 && (
        <div className="card-grid">
          {filteredJobs.map((j) => (
            <JobCard
              key={j.id}
              job={j}
              customer={customer}
              vehicle={vehicleById[j.vehicleId]}
              onSendEstimate={(channel) => handleSendEstimate(j, channel)}
              onSendInvoice={(channel) => handleSendInvoice(j, channel)}
              onMarkPaid={() => handleMarkPaid(j)}
              onStart={() => handleStart(j)}
              onLeave={() => handleLeave(j)}
              onUndoStart={() => handleUndoStart(j)}
              onEdit={() => openEdit(j)}
              onDelete={() => handleDelete(j)}
              onInspect={() => setInspectJob(j)}
              onCancel={() => setCancelJob(j)}
            />
          ))}
        </div>
      )}

      {modalState && (
        <JobModal
          initial={modalState}
          customerId={customerId}
          vehicles={vehicles}
          laborPresets={businessProfile.laborPresets || []}
          taxPresets={businessProfile.taxPresets || []}
          partSearchLinks={businessProfile.partSearchLinks}
          onClose={() => setModalState(null)}
        />
      )}

      {leaveJob && (
        <JobLeaveModal
          job={leaveJob}
          customer={customer}
          businessProfile={businessProfile}
          onClose={() => setLeaveJob(null)}
        />
      )}

      {pendingStart && (
        <ConfirmDialog
          title="Job not approved yet"
          body={`This job is still in "${STATUS_TEXT[pendingStart.status] || 'Draft'}" status — the customer hasn't approved the estimate. You can still start working and send the estimate after.`}
          confirmLabel="Start anyway"
          cancelLabel="Cancel"
          onConfirm={() => {
            const j = pendingStart;
            setPendingStart(null);
            actuallyStart(j);
          }}
          onCancel={() => setPendingStart(null)}
        />
      )}

      {pendingMarkPaid && (
        <ConfirmDialog
          title="Mark this job paid?"
          body={`"${pendingMarkPaid.description}"\n\nThis closes the ticket and moves it to History. Use this when payment came in by cash, Venmo, or any way that didn't go through the invoice link.`}
          confirmLabel="Mark paid"
          cancelLabel="Cancel"
          onConfirm={() => {
            const j = pendingMarkPaid;
            setPendingMarkPaid(null);
            actuallyMarkPaid(j);
          }}
          onCancel={() => setPendingMarkPaid(null)}
        />
      )}

      {inspectJob && (
        <div
          className="modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setInspectJob(null); }}
        >
          <div className="modal-box insp-modal-box" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>Inspection</h2>
              <button
                type="button"
                className="btn-link"
                onClick={() => setInspectJob(null)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <p className="muted small insp-modal-sub">{inspectJob.description}</p>
            <InspectionPanel
              job={inspectJob}
              vehicle={vehicles.find((v) => v.id === inspectJob.vehicleId) || null}
            />
          </div>
        </div>
      )}

      {cancelJobTarget && (
        <CancelJobModal
          job={cancelJobTarget}
          customer={customer}
          onClose={() => setCancelJob(null)}
        />
      )}

      {pendingDeleteCustomer && (
        <ConfirmDialog
          title={`Archive ${pendingDeleteCustomer.name}?`}
          body={
            'They disappear from your customers, your calendar and Home, along with all of '
            + 'their vehicles and jobs. Nothing is deleted — you can bring them back any '
            + 'time from Customers → Archived.'
            + (archiveImpact.owed > 0
              ? `\n\nHeads up: ${formatPrice(archiveImpact.owed)} is still unpaid on their `
                + 'invoices. That drops off Outstanding until you restore them.'
              : '')
            + (archiveImpact.open > 0
              ? `\n\n${archiveImpact.open} open job${archiveImpact.open === 1 ? '' : 's'} `
                + `goes with them.`
              : '')
          }
          confirmLabel="Archive customer"
          cancelLabel="Keep them"
          onConfirm={() => {
            const c = pendingDeleteCustomer;
            setPendingDeleteCustomer(null);
            actuallyArchiveCustomer(c);
          }}
          onCancel={() => setPendingDeleteCustomer(null)}
        />
      )}

      {closedCount > 0 && (
        <p className="muted small" style={{ marginTop: 24, textAlign: 'right' }}>
          {closedCount} closed or cancelled {closedCount === 1 ? 'job' : 'jobs'} hidden — see <Link to="/history">History</Link>.
        </p>
      )}
    </div>
  );
}

// Small one-line "$X labor · $Y parts · $Z supplies" subtitle on the
// card. Hidden when there's nothing meaningful to break down (legacy
// single-line jobs, no parts, no shop supplies).
